import { Action, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { Observable, tap, iif, of, forkJoin, switchMap } from 'rxjs';
import { take } from 'rxjs/operators';
import { EditAATESettings, GetAATESettings } from './aate-setting.action';
import { SystemSettingService } from '@shared/services/system-setting/system-setting.service';
import { ClearAllStates } from '@NgXs/authentication/actions/clear-all-state.action';
import { TargetChartBySituationReportSettings } from '@shared/interfaces/system-settings.interface';
import { SelectedSeason } from '@shared/states/season/season.action';

export interface AATESettingStateModel {
  id: number;
  user: number;
  sob: string;
  series_seq: string;
  target_chart_by_situation_report_settings: TargetChartBySituationReportSettings | null;
  aate: {
    align: string;
    assign: string;
    effort: string;
    tech: string;
  };
  play_info_headings: {
    box1: string;
    box2: string;
    box3: string;
    box4: string;
  };
  efficiency_down_settings: {
    id: number;
    value: number;
    user_setting: number;
    down: number;
    operator: string;
    report: string;
  }[];
  scouting_down_settings: Array<any>;
  scouting_fields: Array<any>;
  scouting_report_columns: object;
}

@State<AATESettingStateModel>({
  name: 'aateSetting',
  defaults: {
    id: null,
    user: null,
    sob: null,
    series_seq: null,
    target_chart_by_situation_report_settings: null,
    aate: {
      align: null,
      assign: null,
      effort: null,
      tech: null,
    },
    play_info_headings: {
      box1: null,
      box2: null,
      box3: null,
      box4: null,
    },
    efficiency_down_settings: null,
    scouting_down_settings: null,
    scouting_fields: null,
    scouting_report_columns: null,
  },
})
@Injectable()
export class AATESettingState {
  constructor(private settingService: SystemSettingService) {}

  @Action(GetAATESettings)
  getAATESettings(
    ctx: StateContext<AATESettingStateModel>,
    { sobValue, seasonId }: GetAATESettings
  ): Observable<any> {
    return this.settingService.getSettingsBySob(sobValue, seasonId).pipe(
      tap((newAateSettings) => {
        if (newAateSettings.length) ctx.patchState(newAateSettings[0]);
        else this.clearState(ctx);
      })
    );
  }

  @Action(EditAATESettings)
  editSeasonDescription(
    { patchState }: StateContext<AATESettingStateModel>,
    { payload }: EditAATESettings
  ) {
    if (payload) patchState(payload);
  }

  @Action(SelectedSeason)
  ensureAATESettingsExist(ctx: StateContext<AATESettingStateModel>, { payload }: SelectedSeason) {
    if (!payload?.id) return;
    return forkJoin([
      this.settingService.getSettingsBySob('O', payload.id),
      this.settingService.getSettingsBySob('D', payload.id),
    ]).pipe(
      take(1),
      switchMap(([oSettings, dSettings]: [any, any]) => {
        const posts: Observable<any>[] = [];
        if (!oSettings || oSettings.length === 0) {
          posts.push(
            this.settingService.postAATESettings({
              play_info_headings: { box1: '', box2: '', box3: '', box4: '' },
              sob: 'O',
              season: payload.id,
            })
          );
        }
        if (!dSettings || dSettings.length === 0) {
          posts.push(
            this.settingService.postAATESettings({
              play_info_headings: { box1: '', box2: '', box3: '', box4: '' },
              sob: 'D',
              season: payload.id,
            })
          );
        }
        if (posts.length > 0) {
          return forkJoin(posts);
        }
        return of(null);
      })
    );
  }

  @Action(ClearAllStates)
  clearState(ctx: StateContext<AATESettingStateModel>) {
    ctx.setState({
      id: null,
      user: null,
      sob: null,
      series_seq: null,
      target_chart_by_situation_report_settings: null,
      aate: {
        align: null,
        assign: null,
        effort: null,
        tech: null,
      },
      play_info_headings: {
        box1: null,
        box2: null,
        box3: null,
        box4: null,
      },
      efficiency_down_settings: null,
      scouting_down_settings: null,
      scouting_fields: null,
      scouting_report_columns: null,
    });
  }
}
